import { AcademicLeague } from "../types/battle"
import { ControlTier } from "../types/faction"
import { LEAGUE_ACCENTS, formatLeagueLabel } from "./leagueInfo"

export const LEAGUE_FACTION_LORE: Record<
  AcademicLeague,
  { idea: string; uiColors: string }
> = {
  TITAN: {
    idea: "Сила, порядок, защита",
    uiColors: "золото / тёплые",
  },
  ATLANT: {
    idea: "Мудрость, связь, стратегия",
    uiColors: "синий / лазурь",
  },
}

export const LEAGUE_PRIVILEGES: Record<
  AcademicLeague,
  { pve: string; map: string; pvp: string }
> = {
  TITAN: {
    pve: "+5% урона по боссам",
    map: "+3 энергии после скана",
    pvp: "+3% очков за победу над чужой лигой",
  },
  ATLANT: {
    pve: "+5% награда за квиз",
    map: "чаще чакра из лута",
    pvp: "−3% потери очков при поражении",
  },
}

export const CONTROL_TIER_LABELS: Record<ControlTier, string> = {
  PARITY: "Паритет",
  EDGE: "Перевес",
  DOMINANCE: "Доминирование",
  HEGEMONY: "Гегемония",
  TOTAL: "Тотальный контроль",
}

export const WAR_PTS_HINTS: { action: string; pts: number }[] = [
  { action: "PvP-победа над чужой лигой (тот же город)", pts: 15 },
  { action: "Скан точки", pts: 3 },
  { action: "Победа над боссом", pts: 10 },
  { action: "Ежедневный тест (участие)", pts: 1 },
]

export const MINORITY_BONUS_LABEL =
  "+10% монет и чакры на первую неделю"

const ROTATION_REWARD_LABELS: Record<string, string> = {
  SHOP_DISCOUNT: "Скидка в магазине",
  QUEST_COINS_BONUS: "Бонус монет за задания",
  SCAN_LOOT_BONUS: "Бонус добычи со скана",
  QR_LIMIT_BONUS: "Бонус лимита 1 на 1",
}

function normalizeRotationRewardKey(key: string): string {
  return key.trim().toUpperCase()
}

export function formatPointsCount(count: number): string {
  const n = Math.abs(Math.trunc(count))
  const mod10 = n % 10
  const mod100 = n % 100
  let word = "очков"
  if (mod100 < 11 || mod100 > 14) {
    if (mod10 === 1) word = "очко"
    else if (mod10 >= 2 && mod10 <= 4) word = "очка"
  }
  return `${count} ${word}`
}

/** Очки вклада в битву за город */
export function formatWarPoints(count: number): string {
  return `${formatPointsCount(count)} битвы`
}

export function formatWarPointsHintLine(action: string, pts: number): string {
  return `${action}: +${formatPointsCount(pts)}`
}

export function formatMilestoneThreshold(threshold: number): string {
  return formatPointsCount(threshold)
}

export function formatMilestoneRewardParts(milestone: {
  coins?: number
  chakra?: number
  fuseBooster?: number
}): string {
  const parts: string[] = []
  if (milestone.coins) parts.push(`${milestone.coins} монет`)
  if (milestone.chakra) parts.push(`${milestone.chakra} чакры`)
  if (milestone.fuseBooster) {
    parts.push(`${milestone.fuseBooster} × усилитель слияния`)
  }
  return parts.join(" · ")
}

export function formatRotationRewardKey(key: string): string {
  return ROTATION_REWARD_LABELS[normalizeRotationRewardKey(key)] ?? key.replace(/_/g, " ").toLowerCase()
}

export const DOMINANCE_REWARD_LABELS = {
  fuse: "Усилитель слияния",
  style: "Токен смены стиля",
} as const

export const TOP_WAR_REWARD_LABELS = {
  coins: "Монеты",
  chakra: "Чакра",
  fuse: "Усилитель слияния",
} as const

export const DOMINANT_LEAGUE_MARKER_COLORS: Record<
  "TITAN" | "ATLANT" | "NEUTRAL",
  { fill: string; border: string }
> = {
  TITAN: { fill: "#FACC15", border: "#CA8A04" },
  ATLANT: { fill: "#38BDF8", border: "#0284C7" },
  NEUTRAL: { fill: "#9CA3AF", border: "#6B7280" },
}

export function formatControlTierLabel(tier: ControlTier | null | undefined): string {
  if (!tier) return "—"
  return CONTROL_TIER_LABELS[tier] ?? tier
}

/** Без дублирования, если label с бэка совпадает с названием ступени. */
export function formatControlTierDisplay(tierInfo: {
  tier: ControlTier
  label?: string | null
}): string {
  const standard = formatControlTierLabel(tierInfo.tier)
  const custom = tierInfo.label?.trim()
  if (custom && custom !== standard) return `${standard} · ${custom}`
  return standard
}

export function formatRotationRewardDescription(
  key: string,
  context: {
    shopDiscountPercent?: number
    isActiveForMe?: boolean
  } = {}
): string {
  const normalized = normalizeRotationRewardKey(key)

  switch (normalized) {
    case "SHOP_DISCOUNT": {
      const percent = context.shopDiscountPercent ?? 0
      const base =
        percent > 0
          ? `Скидка ${percent}% на все товары в магазине`
          : "Скидка на все товары в магазине"
      if (context.isActiveForMe === true) return `${base}. У вас активна`
      if (context.isActiveForMe === false) return `${base}. Сейчас не активна`
      return base
    }
    case "QUEST_COINS_BONUS":
      return "Дополнительные монеты за выполнение ежедневных и недельных заданий"
    case "SCAN_LOOT_BONUS":
      return "Больше наград при сканировании точек на карте"
    case "QR_LIMIT_BONUS": {
      const base = "Увеличен дневной лимит наград за бои 1 на 1"
      if (context.isActiveForMe === true) return `${base}. У вас активен`
      if (context.isActiveForMe === false) return `${base}. Сейчас не активен`
      return base
    }
    default:
      return formatRotationRewardKey(key)
  }
}

export function leagueRowAccent(league: AcademicLeague | null | undefined): string {
  if (!league) return "rgba(255,255,255,0.08)"
  return `${LEAGUE_ACCENTS[league]}22`
}

export function leagueRowBorder(league: AcademicLeague | null | undefined): string {
  if (!league) return "rgba(255,255,255,0.08)"
  return `${LEAGUE_ACCENTS[league]}55`
}

export function formatTournamentKindLabel(
  kind: string | null | undefined,
  league?: AcademicLeague | null
): string {
  switch (kind) {
    case "LEAGUE_TITAN":
      return `Финал ${formatLeagueLabel("TITAN")}`
    case "LEAGUE_ATLANT":
      return `Финал ${formatLeagueLabel("ATLANT")}`
    case "CITY_FINAL":
      return "Финал города"
    default:
      return kind ?? "Турнир"
  }
}
